import { useGameStore } from "../store/gameStore";
import {
  RewardReference,
  StageInitData,
  Caravan,
  CaravanElement,
} from "../handlers/types";

/**
 * Service that provides stage-related functionality using the game store
 * This is a lightweight wrapper around the store's stage methods
 */
export default class StageService {
  private static instance: StageService;

  private constructor() {
    // No longer need to initialize anything - using store directly
  }

  /**
   * Get the StageService singleton instance
   */
  public static getInstance(): StageService {
    if (!StageService.instance) {
      StageService.instance = new StageService();
    }
    return StageService.instance;
  }

  /**
   * Initialize the stage service by fetching stage data
   * @param stageId - The ID of the stage to initialize
   */
  public async init(stageId: string): Promise<StageInitData> {
    const stageData = await useGameStore.getState().initializeStage(stageId);
    if (!stageData) {
      throw new Error("Failed to initialize stage data");
    }
    return stageData;
  }

  /**
   * Returns and removes a boss ID from the boss list
   * @returns An object containing boss ID and value
   */
  public popBossId(): RewardReference | undefined {
    return useGameStore.getState().popBossId() || undefined;
  }

  /**
   * Returns and removes a treasure ID from the treasure list
   * @returns An object containing treasure ID and value
   */
  public popTreasureId(): RewardReference | undefined {
    return useGameStore.getState().popTreasureId() || undefined;
  }

  /**
   * Returns the endgame ID and value
   * @returns The endgame RewardReference object
   */
  public getEndgameId(): RewardReference | undefined {
    return useGameStore.getState().getEndgameId() || undefined;
  }

  /**
   * Returns the gift coins reference with ID and max value
   * @returns The gift coins RewardReference object
   */
  public getGiftCoinsId(): RewardReference | undefined {
    return useGameStore.getState().getGiftCoinsId() || undefined;
  }

  /**
   * Returns the caravan data with ID and pool
   * @returns The Caravan object
   */
  public getCaravan(): Caravan | undefined {
    return useGameStore.getState().getCaravan() || undefined;
  }

  /**
   * Gets the next caravan element from the pool
   * @returns A CaravanElement or undefined if pool is empty
   */
  public popCaravanElement(): CaravanElement | undefined {
    return useGameStore.getState().popCaravanElement() || undefined;
  }

  /**
   * Checks if the caravan pool has any elements left
   * @returns True if the caravan pool has elements, false otherwise
   */
  public hasCaravanElements(): boolean {
    return useGameStore.getState().hasCaravanElements();
  }

  /**
   * Claims a reward by sending the ID to the server
   * @param id - The encrypted ID of the reward to claim
   * @param value - Optional value associated with the reward
   * @returns A promise that resolves to whether the claim was successful
   */
  public async claim(id: string, value?: number): Promise<boolean> {
    return useGameStore.getState().claimReward(id, value);
  }

  /**
   * Check if the stage data has been initialized
   */
  public isInitialized(): boolean {
    return useGameStore.getState().isStageInitialized;
  }

  /**
   * Get the current run ID
   */
  public getRunId(): string | undefined {
    return useGameStore.getState().getRunId() || undefined;
  }

  /**
   * Clear the stage data (used when returning to main menu)
   */
  public clearStageData(): void {
    useGameStore.getState().clearStageData();
  }

  /**
   * Get the raw stage data
   */
  public getStageData(): StageInitData | null {
    return useGameStore.getState().stageData;
  }
}
